import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Clock, Paperclip, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Spinner, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Badge } from "../../components/ui/badge";
import { api, errMsg, fmtDate, uploadFileB64, fileHref } from "../../lib/api";

export default function Grievances() {
  const { code } = useParams();
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "", subject: "", description: "", property_pin: "", priority: "medium" });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reopenId, setReopenId] = useState(null);
  const [reopenRemarks, setReopenRemarks] = useState("");

  const load = useCallback(() => api.get(`/t/${code}/grievances`).then((r) => setItems(r.data)), [code]);
  useEffect(() => {
    load().catch(() => {});
    api.get(`/t/${code}/grievance-categories`).then((r) => setCats(r.data)).catch(() => {});
  }, [load, code]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, 5 - attachments.length)) {
        const up = await uploadFileB64(code, file);
        setAttachments((a) => [...a, up]);
      }
      toast.success("Attachment uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Select a category");
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/grievances`, { ...form, attachments });
      toast.success(`Grievance ${r.data.ticket_number} submitted`);
      setOpen(false);
      setForm({ category: "", subject: "", description: "", property_pin: "", priority: "medium" });
      setAttachments([]);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async (gId) => {
    try {
      await api.post(`/t/${code}/grievances/${gId}/reopen`, { remarks: reopenRemarks || null });
      toast.success("Grievance reopened");
      setReopenId(null);
      setReopenRemarks("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Grievances</h1>
          <p className="text-muted-foreground text-sm">Report civic issues and track resolution</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-grievance-button"><Plus className="h-4 w-4 mr-2" /> New Grievance</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Submit grievance</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="grievance-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Subject *</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="grievance-subject-input" />
              </div>
              <div className="space-y-1">
                <Label>Description *</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="grievance-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Related property PIN</Label>
                  <Input value={form.property_pin} onChange={(e) => setForm({ ...form, property_pin: e.target.value })} placeholder="Optional" />
                </div>
                <div className="space-y-1">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Photos / documents (max 5)</Label>
                {attachments.length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-1">
                    {attachments.map((a, i) => (
                      <span key={i} className="text-xs flex items-center gap-1 border rounded-full px-2.5 py-1 bg-muted/50">
                        <Paperclip className="h-3 w-3" />
                        <a href={fileHref(a.url)} target="_blank" rel="noreferrer" className="hover:underline underline-offset-2"
                          title="View attachment" data-testid={`grievance-attachment-view-${i}`}>{a.filename}</a>
                        <button type="button" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} data-testid={`grievance-attachment-remove-${i}`}>
                          <X className="h-3 w-3 text-destructive" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {attachments.length < 5 && (
                  <Input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" multiple disabled={uploading}
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="grievance-attachment-input" />
                )}
                {uploading && <p className="text-xs text-muted-foreground">Uploading...</p>}
              </div>
              <Button type="submit" className="w-full" disabled={busy || uploading} data-testid="citizen-grievance-submit-button">
                {busy ? "Submitting..." : "Submit grievance"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!items ? <Spinner /> : items.length === 0 ? (
        <EmptyState title="No grievances yet" subtitle="Raise a grievance for any civic issue in your area" />
      ) : (
        <Accordion type="single" collapsible className="space-y-3" data-testid="citizen-grievances-list">
          {items.map((g) => (
            <AccordionItem key={g.id} value={g.id} className="border rounded-xl bg-card shadow-sm px-4">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center justify-between w-full pr-3 gap-2 flex-wrap">
                  <div className="text-left">
                    <p className="font-mono text-sm font-medium">{g.ticket_number}</p>
                    <p className="text-sm">{g.subject}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {g.assigned_to_name && <Badge variant="outline" className="text-[10px]">Assigned: {g.assigned_to_name}</Badge>}
                    <StatusBadge status={g.status} />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <p className="text-sm text-muted-foreground mb-3">{g.description}</p>
                {(g.attachments || []).length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-3">
                    {g.attachments.map((a, i) => (
                      <a key={i} href={fileHref(a.url)} target="_blank" rel="noreferrer"
                        className="text-xs flex items-center gap-1 border rounded-full px-2.5 py-1 hover:bg-muted"
                        data-testid={`grievance-attachment-link-${i}`}>
                        <Paperclip className="h-3 w-3" /> {a.filename || `Attachment ${i + 1}`}
                      </a>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> SLA deadline: {fmtDate(g.sla_deadline)}
                </p>
                <div className="space-y-0">
                  {(g.timeline || []).map((t, i) => (
                    <div key={i} className="flex gap-3 pb-3 relative">
                      <div className="flex flex-col items-center">
                        <div className="h-2.5 w-2.5 rounded-full bg-primary mt-1" />
                        {i < g.timeline.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium capitalize">{t.status.replace(/_/g, " ")}</p>
                        <p className="text-xs text-muted-foreground">{t.remarks || ""} • by {t.by} • {fmtDate(t.at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {["resolved", "closed"].includes(g.status) && (
                  reopenId === g.id ? (
                    <div className="flex gap-2 mt-2">
                      <Input placeholder="Why are you reopening? (optional)" value={reopenRemarks}
                        onChange={(e) => setReopenRemarks(e.target.value)} data-testid="grievance-reopen-remarks" />
                      <Button size="sm" onClick={() => reopen(g.id)} data-testid="grievance-reopen-confirm">Reopen</Button>
                      <Button size="sm" variant="outline" onClick={() => setReopenId(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setReopenId(g.id)} data-testid={`grievance-reopen-${g.ticket_number}`}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Not satisfied? Reopen
                    </Button>
                  )
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
